import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Package, AlertTriangle } from "lucide-react";
import { api } from "../../lib/api";
import { StockHistory } from "./StockHistory";

const opts = { withCredentials: true };

const Cell = ({ item, onSave, low, testid, linked }) => {
  const [v, setV] = useState(item ? String(item.quantity) : "");
  useEffect(() => setV(item ? String(item.quantity) : ""), [item]);
  const isLow = item && item.quantity <= low;
  if (!linked) return <td className="p-2 text-center text-[#757575] text-[10px] italic" title="Librairie non rattachée à ce livre" data-testid={`${testid}-unlinked`}>non rattachée</td>;
  const commit = () => { if (v !== (item ? String(item.quantity) : "") && v !== "") onSave(Math.max(0, parseInt(v, 10) || 0)); };
  return (
    <td className="p-2 text-center">
      <input type="number" min={0} value={v} placeholder="—" onChange={(e) => setV(e.target.value)} onBlur={commit} onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
        data-testid={testid} className={`w-16 text-center border px-1 py-1 bg-[#FAF9F6] ${isLow ? "border-[#9E4735] text-[#9E4735] font-semibold" : "border-[#D1CFC7]"}`} />
      {isLow && <span data-testid={`${testid}-low`} className="block mt-1 text-[9px] uppercase tracking-wider text-[#9E4735]">Stock faible</span>}
    </td>
  );
};

export function StockManager() {
  const [d, setD] = useState(null);
  const [tick, setTick] = useState(0);
  const load = () => api.get("/admin/stock", opts).then((r) => { setD(r.data); setTick((t) => t + 1); }).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!d) return null;
  const find = (pid, eid) => d.items.find((i) => i.pickup_id === pid && i.extract_id === eid);
  const save = (pickup_id, extract_id) => (quantity) =>
    api.put("/admin/stock", { pickup_id, extract_id, quantity }, opts).then(() => { toast.success("Stock mis à jour"); load(); }).catch(() => toast.error("Mise à jour impossible"));

  return (
    <section data-testid="stock-manager" className="font-body">
      <div className="flex flex-wrap items-center gap-3 mb-2">
        <h3 className="font-serif text-2xl font-light flex items-center gap-2"><Package size={20} className="text-[#2C402E]" /> Stock papier par point de retrait</h3>
        {d.low_count > 0 && <span data-testid="stock-low-count" className="flex items-center gap-1 bg-[#9E4735] text-[#FAF9F6] text-[11px] px-2 py-1"><AlertTriangle size={12} /> {d.low_count} stock(s) faible(s)</span>}
      </div>
      <p className="text-xs text-[#757575] mb-4 max-w-3xl">Saisissez le nombre d'exemplaires déposés (laissez « — » pour ne pas suivre). Le stock baisse de 1 quand une commande papier de ce point passe à « Livrée » et remonte si elle est annulée ou supprimée. Un e-mail vous prévient à {d.low_threshold} exemplaires ou moins. « non rattachée » = librairie non cochée dans la fiche de l'extrait.</p>
      {d.points.length === 0 || d.books.length === 0 ? <p className="text-sm text-[#757575]" data-testid="stock-empty">Ajoutez d'abord des points de retrait et des livres disponibles en papier.</p> : (
        <div className="overflow-x-auto border border-[#D1CFC7]">
          <table className="text-xs whitespace-nowrap">
            <thead className="bg-[#F4F1EA] text-[#4A4A4A]">
              <tr><th className="p-3 text-left">Point de retrait</th>{d.books.map((b) => <th key={b.id} className="p-3 max-w-[160px] truncate">{b.title}</th>)}</tr>
            </thead>
            <tbody>
              {d.points.map((p) => (
                <tr key={p.id} className="border-t border-[#E8E4D9]">
                  <td className="p-3"><strong>{p.name}</strong><span className="block text-[#757575]">{p.city}</span></td>
                  {d.books.map((b) => <Cell key={b.id} linked={!!b.pickup_ids?.includes(p.id)} item={find(p.id, b.id)} low={d.low_threshold} onSave={save(p.id, b.id)} testid={`stock-${p.id}-${b.id}`} />)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <StockHistory points={d.points} refreshKey={tick} />
    </section>
  );
}
